import React, { useEffect, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Storefront, MapPin, Plus, PencilSimple, Trash, Users as UsersIcon, CurrencyInr, Warning } from "@phosphor-icons/react";

const EMPTY = {
  name: "", city: "", address: "", phone: "", email: "",
  gstin: "", state_code: "", store_manager_id: "", active: true,
};

export default function Stores() {
  const { user, reloadStores } = useAuth();
  const [stores, setStores] = useState([]);
  const [summary, setSummary] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, u, sum] = await Promise.all([
        api.get("/stores"),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/stores/summary").catch(() => ({ data: { stores: [] } })),
      ]);
      setStores(s.data);
      setUsers(u.data.filter((x) => ["manager", "admin"].includes(x.role)));
      setSummary(sum.data.stores || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "", city: s.city || "", address: s.address || "",
      phone: s.phone || "", email: s.email || "",
      gstin: s.gstin || "", state_code: s.state_code || "",
      store_manager_id: s.store_manager_id || "", active: s.active !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.city.trim()) {
      toast.error("Name and City are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/stores/${editing.id}`, form);
        toast.success("Store updated");
      } else {
        await api.post("/stores", form);
        toast.success("Store created");
      }
      setOpen(false);
      await load();
      await reloadStores();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete store "${s.name}, ${s.city}"? Any users still assigned will block the deletion.`)) return;
    try {
      await api.delete(`/stores/${s.id}`);
      toast.success("Store deleted");
      await load();
      await reloadStores();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const managerName = (mid) => users.find((u) => u.id === mid)?.name || "—";

  // Aggregate across all stores (admin view)
  const totals = summary.reduce(
    (a, s) => ({
      total_sales: a.total_sales + s.total_sales,
      outstanding: a.outstanding + s.outstanding,
      temp_pending: a.temp_pending + s.temp_pending,
      invoice_count: a.invoice_count + s.invoice_count,
      temp_count: a.temp_count + s.temp_count,
      employee_count: a.employee_count + s.employee_count,
    }),
    { total_sales: 0, outstanding: 0, temp_pending: 0, invoice_count: 0, temp_count: 0, employee_count: 0 }
  );

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Administration</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Stores</h1>
          <p className="text-sm text-muted-foreground mt-1">
            City-wise branches. Assign a Store Manager per store; Store Managers and Employees see data from
            their store only. Admins can switch between "All Stores" and any single store from the top bar.
          </p>
        </div>
        {user?.role === "admin" && (
          <Button onClick={openNew} data-testid="store-new-btn" className="h-9">
            <Plus size={16} className="mr-1.5" /> New Store
          </Button>
        )}
      </div>

      {/* All-stores aggregate strip (admin only) */}
      {user?.role === "admin" && summary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="All-Stores Sales" value={inr(totals.total_sales)} sub={`${totals.invoice_count} invoices`} tone="primary" />
          <StatCard label="Outstanding" value={inr(totals.outstanding)} sub="Receivables" tone="destructive" />
          <StatCard label="Payment Pending" value={inr(totals.temp_pending)} sub={`${totals.temp_count} temps`} tone="warning" />
          <StatCard label="Store Staff" value={totals.employee_count} sub={`${summary.length} store${summary.length !== 1 ? "s" : ""}`} tone="muted" />
        </div>
      )}

      {/* Per-store grid */}
      {summary.length === 0 ? (
        <div className="card-flat p-10 text-center">
          <Storefront size={40} className="mx-auto text-muted-foreground mb-3" />
          <div className="font-heading text-lg font-semibold">No stores yet</div>
          <p className="text-sm text-muted-foreground mt-1">Add your first store to enable city-wise tracking and staff scoping.</p>
          {user?.role === "admin" && (
            <Button onClick={openNew} className="mt-4 h-9" data-testid="store-empty-new-btn">
              <Plus size={16} className="mr-1.5" /> New Store
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {summary.map((s) => {
            const store = stores.find((x) => x.id === s.id) || s;
            return (
              <div key={s.id} className="card-flat p-5 flex flex-col gap-3" data-testid={`store-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Storefront size={18} className="text-primary" />
                      <div className="font-heading text-base font-semibold tracking-tight">{s.name}</div>
                    </div>
                    <div className="flex items-center gap-1 text-[12px] text-muted-foreground mt-0.5">
                      <MapPin size={12} weight="regular" /> {s.city}
                    </div>
                  </div>
                  {!s.active && <span className="pill pill-muted text-[10px]">INACTIVE</span>}
                </div>

                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <div>
                    <div className="eyebrow text-[10px]">Sales</div>
                    <div className="font-mono font-semibold">{inr(s.total_sales)}</div>
                    <div className="text-[10.5px] text-muted-foreground">{s.invoice_count} invoices</div>
                  </div>
                  <div>
                    <div className="eyebrow text-[10px]">Outstanding</div>
                    <div className={`font-mono font-semibold ${s.outstanding > 0 ? "text-destructive" : ""}`}>{inr(s.outstanding)}</div>
                    <div className="text-[10.5px] text-muted-foreground">Receivables</div>
                  </div>
                  <div>
                    <div className="eyebrow text-[10px]">Pending Payments</div>
                    <div className="font-mono font-semibold text-amber-600">{inr(s.temp_pending)}</div>
                    <div className="text-[10.5px] text-muted-foreground">{s.temp_count} temps</div>
                  </div>
                  <div>
                    <div className="eyebrow text-[10px]">Manager</div>
                    <div className="text-[12.5px] font-medium truncate">{managerName(s.store_manager_id)}</div>
                    <div className="text-[10.5px] text-muted-foreground flex items-center gap-1">
                      <UsersIcon size={11} /> {s.employee_count} staff
                    </div>
                  </div>
                </div>

                {store.phone || store.address ? (
                  <div className="text-[11.5px] text-muted-foreground border-t border-border pt-2 leading-relaxed">
                    {store.address && <div>{store.address}</div>}
                    {store.phone && <div>Phone: {store.phone}</div>}
                  </div>
                ) : null}

                {user?.role === "admin" && (
                  <div className="flex items-center gap-2 pt-1">
                    <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={() => openEdit(store)} data-testid={`store-edit-${s.id}`}>
                      <PencilSimple size={13} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-[12px] text-destructive border-destructive/40" onClick={() => remove(store)} data-testid={`store-delete-${s.id}`}>
                      <Trash size={13} className="mr-1" /> Delete
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Store" : "New Store"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="eyebrow">Store Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="store-form-name" placeholder="e.g. Vetrich Chennai Branch" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">City *</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="store-form-city" placeholder="Chennai" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="store-form-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="eyebrow">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="store-form-phone" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="store-form-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="eyebrow">GSTIN</Label>
                <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="font-mono uppercase" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">State Code</Label>
                <Input value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })} className="font-mono" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Store Manager</Label>
              <Select value={form.store_manager_id || "__none__"} onValueChange={(v) => setForm({ ...form, store_manager_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="store-form-manager"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Only users with role Store Manager / Manager / Admin are shown.</p>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-[11px] text-muted-foreground">Inactive stores are hidden from switcher.</div>
              </div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="store-form-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-9">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="store-form-save" className="h-9">
              {saving ? "Saving…" : editing ? "Update Store" : "Create Store"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, sub, tone = "muted" }) {
  const toneClass = {
    primary: "border-primary/30 bg-primary/5",
    destructive: "border-destructive/30 bg-destructive/5",
    warning: "border-amber-300 bg-amber-50",
    muted: "border-border bg-muted/30",
  }[tone] || "border-border bg-muted/30";
  return (
    <div className={`rounded-lg border p-4 ${toneClass}`}>
      <div className="eyebrow text-[10px]">{label}</div>
      <div className="font-heading text-xl font-bold tracking-tight mt-1">{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}
